/**
 * Review Queue Card Component
 *
 * Displays manual review queue metrics including pending count, approval/rejection stats,
 * and average confidence score.
 */

import React from 'react';
import { Paper, Title, Group, Stack, Text, Badge, Loader, Button, SimpleGrid } from '@mantine/core';
import { IconChecklist, IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { ReviewQueueSection } from '../../../../domain/entities/AdminDashboard';

interface ReviewQueueCardProps {
  reviewQueue: ReviewQueueSection;
  isLoading?: boolean;
}

/**
 * Formats confidence score as percentage (0-100)
 */
function formatConfidence(score: number): string {
  return `${Math.round(score * 100)}%`;
}

export const ReviewQueueCard: React.FC<ReviewQueueCardProps> = ({
  reviewQueue,
  isLoading = false
}): React.ReactElement => {
  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconChecklist size={24} />
            <Title order={3}>Review Queue</Title>
          </Group>
          {reviewQueue.pendingCount > 0 && (
            <Badge color="orange" variant="filled" size="lg">
              {reviewQueue.pendingCount} pending
            </Badge>
          )}
        </Group>

        {isLoading ? (
          <Loader size="sm" />
        ) : (
          <>
            <SimpleGrid cols={2} spacing="xs">
              <Stack gap={4}>
                <Text size="xs" c="dimmed">
                  Approved (24h)
                </Text>
                <Text size="lg" fw={700} c="green">
                  {reviewQueue.approvedLast24Hours}
                </Text>
              </Stack>

              <Stack gap={4}>
                <Text size="xs" c="dimmed">
                  Rejected (24h)
                </Text>
                <Text size="lg" fw={700} c="red">
                  {reviewQueue.rejectedLast24Hours}
                </Text>
              </Stack>
            </SimpleGrid>

            <Group justify="space-between" mt="xs">
              <Text size="sm" c="dimmed">
                Avg Confidence:
              </Text>
              <Badge color="blue" variant="light">
                {formatConfidence(reviewQueue.averageConfidenceScore)}
              </Badge>
            </Group>

            {reviewQueue.pendingCount > 0 && (
              <Button
                component={Link}
                to="/admin/mentions/review"
                variant="light"
                color="blue"
                fullWidth
                rightSection={<IconExternalLink size={16} />}
              >
                Review Pending Mentions
              </Button>
            )}

            {reviewQueue.pendingCount === 0 && (
              <Text size="sm" c="dimmed" ta="center" mt="xs">
                No mentions pending review
              </Text>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
